import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link2, Plus, Sparkles, Wand2, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';
import type {
  CaptionDraftGrounding,
  CaptionDraftReadReference,
  CaptionDraftSkippedReference,
  CaptionDraftsResult,
  CaptionQuestion,
  CaptionQuestionAnswer,
  CaptionRefineResult,
  PostMedia,
} from '../../types/publishing';
import type { PostDestinationId } from '../../lib/publishing/destinations';
import {
  CAPTION_DRAFT_STYLES,
  EMOJI_LEVELS,
  TONE_SUGGESTIONS,
  captionDraftAvailability,
  captionStyleLabel,
  type CaptionEmojiLevel,
} from '../../lib/publishing/captionDrafting';
import { captionQuestions, draftCaption, refineCaption } from './api';
import { draftingWaitLabel, useElapsedSeconds } from './useElapsedSeconds';

// Publishing composer — the AI caption WORKSPACE (round 3, 2026-08-07).
//
// ⚠️ IT IS A FULL-WIDTH REGION, NOT A MODAL AND NOT THE OLD NARROW PANEL. Round 2
// rendered several-hundred-word drafts in a ~400px box inside the composer
// column, which made them unreadable and impossible to compare — so the AM could
// not actually use the choice the three styles exist to give them. It is also
// deliberately NOT a modal (Scott's call): a modal would hide the composer the
// drafts are for, and the caption box is where the chosen draft lands.
//
// Vertical flow: INPUTS → QUESTIONS (when the model asked any) → DRAFTS → REFINE.
//
// ⚠️⚠️ ON-DEMAND ONLY. No useEffect, no useQuery: every call fires from a click.
// Chaining questions → drafting happens inside a MUTATION CALLBACK, which is
// still the same user action, never an effect. Pinned by the source-shape guard
// captionDraftOnDemandOnly.test.ts.

const MAX_REFERENCE_URLS = 3;
/** The value the "other" option carries; anything else is a suggested answer. */
const OTHER = '__other__';

// ---------------------------------------------------------------------------
// Trigger — what sits under the caption box when the workspace is closed
// ---------------------------------------------------------------------------

export function CaptionWorkspaceTrigger({
  kyckAccountId,
  destinations,
  open,
  onOpen,
  className,
}: {
  kyckAccountId: string;
  destinations: PostDestinationId[];
  open: boolean;
  onOpen: () => void;
  className?: string;
}) {
  const availability = captionDraftAvailability(destinations);

  // ⚠️ Stories publish no caption field at all, so there is nothing to draft.
  // Say why rather than showing a control whose output goes nowhere.
  if (!availability.available) {
    return (
      <p className={cn('text-xs text-slate-400', className)}>
        <Sparkles className="mr-1 inline h-3 w-3" />
        {availability.reason}
      </p>
    );
  }

  const noAccount = kyckAccountId === '';
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onOpen}
        disabled={noAccount || open}
        title={
          noAccount
            ? 'Choose an account first — the drafts are written in that account’s voice'
            : 'Draft caption options with AI'
        }
      >
        <Sparkles className="h-3.5 w-3.5" />
        {open ? 'Drafting workspace open above' : 'Draft caption'}
      </Button>
      {noAccount && (
        <span className="text-xs text-slate-400">
          Choose an account first — drafts follow that account&apos;s voice.
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reference reporting
// ---------------------------------------------------------------------------

/**
 * What we read, and what we couldn't.
 *
 * ⚠️⚠️ THE POSITIVE CASE IS NOT DECORATION. In round 1 the panel said nothing
 * about references at all: a page whose body we entirely failed to read looked
 * exactly like a page we read in full, and the AM had no way to tell before
 * trusting the draft. Showing the extracted LENGTH is what makes a bad read
 * visible — a review that comes back as "read 180 characters" is obviously wrong
 * at a glance, where "read 1 reference" is not.
 */
function ReferenceReport({
  read,
  skipped,
}: {
  read: CaptionDraftReadReference[];
  skipped: CaptionDraftSkippedReference[];
}) {
  if (read.length === 0 && skipped.length === 0) return null;
  return (
    <div className="space-y-0.5">
      {read.length > 0 && (
        <p className="text-xs text-slate-600">
          Read {read.length} {read.length === 1 ? 'reference' : 'references'} —{' '}
          {read.map((ref) => `${ref.chars.toLocaleString()} characters`).join(', ')}.
        </p>
      )}
      {skipped.map((skip) => (
        <p key={skip.url} className="text-xs text-amber-700">
          Couldn&apos;t read <span className="font-medium break-all">{skip.url}</span> —{' '}
          {skip.reason}.
        </p>
      ))}
    </div>
  );
}

function groundingLabel(grounding: CaptionDraftGrounding, exampleCount: number): string {
  switch (grounding) {
    case 'account':
      return `Voice matched to this account's last ${exampleCount} published ${
        exampleCount === 1 ? 'post' : 'posts'
      }.`;
    case 'brand':
      return `No published posts on this account yet — voice matched to ${exampleCount} recent ${
        exampleCount === 1 ? 'post' : 'posts'
      } from the same brand.`;
    default:
      return 'No past posts to match — read the tone carefully before publishing.';
  }
}

function SectionHeading({ step, title }: { step: string; title: string }) {
  return (
    <div className="mb-2 flex items-baseline gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {step}
      </span>
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The workspace
// ---------------------------------------------------------------------------

export function CaptionWorkspace({
  kyckAccountId,
  destinations,
  media,
  currentCaption,
  sharedAcrossAccounts = false,
  onUse,
  onClose,
}: {
  kyckAccountId: string;
  destinations: PostDestinationId[];
  media: PostMedia[];
  /**
   * The caption box's LIVE value. Refine works on this rather than on the draft
   * we last returned, so an AM who tweaks a line by hand and then asks for
   * "shorter" gets their edit refined, not silently discarded.
   */
  currentCaption: string;
  /** true on the multi-account composer: one caption, several businesses. */
  sharedAcrossAccounts?: boolean;
  /** Populates the caption box. Never publishes. */
  onUse: (caption: string) => void;
  onClose: () => void;
}) {
  const [brief, setBrief] = useState('');
  const [tone, setTone] = useState('');
  // ⚠️ 'light' is the default AND means "let the account's examples lead" —
  // see CaptionEmojiLevel. It is not the same as choosing Light explicitly
  // being a no-op: None and Generous are the overrides.
  const [emoji, setEmoji] = useState<CaptionEmojiLevel>('light');
  const [urls, setUrls] = useState<string[]>(['']);
  const [questions, setQuestions] = useState<CaptionQuestion[] | null>(null);
  // Per question: the chosen suggestion, or OTHER plus the typed text.
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [result, setResult] = useState<CaptionDraftsResult | null>(null);
  const [used, setUsed] = useState<number | null>(null);
  const [instruction, setInstruction] = useState('');
  const [refineReport, setRefineReport] = useState<CaptionRefineResult | null>(null);

  function answersFor(list: CaptionQuestion[] | null): CaptionQuestionAnswer[] {
    if (!list) return [];
    return list
      .map((q) => ({
        question: q.question,
        answer: picked[q.id] === OTHER ? (typed[q.id] ?? '') : (picked[q.id] ?? ''),
      }))
      .filter((a) => a.answer.trim() !== '');
  }

  /** The shared half of every request — the composition being written about. */
  const composition = (answers: CaptionQuestionAnswer[]) => ({
    kyckAccountId,
    destinations,
    brief,
    tone,
    emoji,
    referenceUrls: urls.map((u) => u.trim()).filter((u) => u !== ''),
    media,
    answers,
    sharedAcrossAccounts,
  });

  const generate = useMutation({
    mutationFn: (answers: CaptionQuestionAnswer[]) => draftCaption(composition(answers)),
    onSuccess: (data) => {
      setResult(data);
      setUsed(null);
      setRefineReport(null);
    },
  });

  const ask = useMutation({
    mutationFn: () => captionQuestions(composition([])),
    onSuccess: (data) => {
      // ⚠️ NO QUESTIONS IS A NORMAL ANSWER — the model had everything it needed,
      // or the call degraded. Either way the AM gets drafts without another
      // click. This chains inside a mutation callback, not an effect: it is
      // still the same user action they started.
      if (data.questions.length === 0) {
        generate.mutate([]);
        return;
      }
      setQuestions(data.questions);
    },
    // A failed questions call must never strand the AM. Draft anyway.
    onError: () => generate.mutate([]),
  });

  // ⚠️ STATELESS: each refine sends the LIVE caption plus one instruction, never
  // an accumulating conversation — so cost stays flat however many rounds the AM
  // wants, and every round stays grounded in the original brief and references.
  const refine = useMutation({
    mutationFn: () =>
      refineCaption({
        ...composition(answersFor(questions)),
        currentDraft: currentCaption,
        instruction,
      }),
    onSuccess: (data) => {
      onUse(data.draft);
      setRefineReport(data);
      setInstruction('');
    },
  });

  const busy = ask.isPending || generate.isPending;
  const waiting = useElapsedSeconds(busy);
  const errorMessage =
    generate.isError ? (generate.error as Error).message : null;
  const refineError = refine.isError ? (refine.error as Error).message : null;

  const imageNote =
    media.length === 0
      ? 'No media attached yet'
      : media[0]?.type === 'video'
        ? 'Video — the AI sees a still frame at most, never the footage'
        : `Image attached${media.length > 1 ? ` (${media.length}; the first is used)` : ''}`;

  return (
    <section
      id="caption-workspace"
      className="mb-6 rounded-xl border border-kyck-200 bg-kyck-50/30 p-4 lg:p-5"
    >
      <div className="mb-4 flex items-center gap-2 border-b border-kyck-200/70 pb-3">
        <Sparkles className="h-4 w-4 shrink-0 text-kyck-600" />
        <h2 className="text-sm font-semibold text-slate-800">Draft a caption with AI</h2>
        <span className="hidden text-xs text-slate-500 sm:inline">
          — three approaches to the same post; pick one to edit
        </span>
        <span className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
        >
          <X className="h-3.5 w-3.5" />
          Close
        </button>
      </div>

      {/* ---------------- 1 · Inputs ---------------- */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <SectionHeading step="1" title="What's this post about?" />
          <Textarea
            id="caption-brief"
            aria-label="What's this post about?"
            value={brief}
            rows={4}
            placeholder="EOFY sale — lead with the 0% finance offer, mention it ends Sunday"
            onChange={(e) => setBrief(e.target.value)}
          />
          <p className="mt-1 text-[11px] text-slate-500">
            Anything you state here can be used as fact. Prices, finance terms and dates will
            <strong className="font-medium"> only</strong> appear if you put them here or in a
            link.
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="caption-tone">
              Tone <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <input
              id="caption-tone"
              type="text"
              value={tone}
              placeholder="Punchy and tradie-friendly"
              className="block h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
              onChange={(e) => setTone(e.target.value)}
            />
            <div className="mt-1 flex flex-wrap gap-1">
              {TONE_SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion.label}
                  type="button"
                  // ⚠️ Fills the field with the FULL DESCRIPTIVE PHRASE, not the
                  // label: "Premium" is a word the model has to guess at, the
                  // phrase is an instruction. Sets rather than appends — a
                  // starting point the AM edits, not a closed vocabulary.
                  onClick={() => setTone(suggestion.phrase)}
                  title={suggestion.phrase}
                  className={cn(
                    'rounded-full border px-2 py-0.5 text-[11px] transition-colors',
                    tone === suggestion.phrase
                      ? 'border-kyck-400 bg-kyck-50 text-kyck-700'
                      : 'border-slate-200 bg-white text-slate-500 hover:border-kyck-300 hover:text-kyck-700',
                  )}
                >
                  {suggestion.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="mb-1 block text-xs font-medium text-slate-600">Emoji</span>
            <div className="flex flex-wrap gap-1">
              {EMOJI_LEVELS.map((level) => (
                <button
                  key={level.id}
                  type="button"
                  onClick={() => setEmoji(level.id)}
                  title={level.hint}
                  aria-pressed={emoji === level.id}
                  className={cn(
                    'rounded-full border px-2.5 py-0.5 text-[11px] transition-colors',
                    emoji === level.id
                      ? 'border-kyck-400 bg-kyck-50 text-kyck-700'
                      : 'border-slate-200 bg-white text-slate-500 hover:border-kyck-300 hover:text-kyck-700',
                  )}
                >
                  {level.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="mb-1 block text-xs font-medium text-slate-600">
              Reference links <span className="font-normal text-slate-400">(optional)</span>
            </span>
            <div className="space-y-1.5">
              {urls.map((url, index) => (
                <div key={index} className="flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <input
                    type="url"
                    value={url}
                    placeholder="https://…  a listing, offer or announcement page"
                    className="block h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                    onChange={(e) =>
                      setUrls((current) =>
                        current.map((u, i) => (i === index ? e.target.value : u)),
                      )
                    }
                  />
                  {urls.length > 1 && (
                    <button
                      type="button"
                      title="Remove this link"
                      className="shrink-0 text-slate-400 hover:text-slate-700"
                      onClick={() => setUrls((c) => c.filter((_, i) => i !== index))}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {urls.length < MAX_REFERENCE_URLS && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-kyck-700"
                  onClick={() => setUrls((c) => [...c, ''])}
                >
                  <Plus className="h-3 w-3" />
                  Add another link
                </button>
              )}
            </div>
          </div>

          <p className="text-[11px] text-slate-500">{imageNote}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button type="button" onClick={() => ask.mutate()} disabled={busy}>
          {busy ? <Spinner className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
          {result ? 'Start again' : 'Draft captions'}
        </Button>
        {/* ⚠️ ALWAYS AVAILABLE. The questions are a quality lift, not a gate — an
            AM in a hurry goes straight to drafts. */}
        <Button
          type="button"
          variant="outline"
          onClick={() => generate.mutate([])}
          disabled={busy}
          title="Skip the questions and write the captions now"
        >
          Just write it
        </Button>
        {busy && <span className="text-xs text-slate-500">{draftingWaitLabel(waiting)}</span>}
        {errorMessage && <span className="text-xs text-amber-700">{errorMessage}</span>}
      </div>

      {/* ---------------- 2 · Questions ---------------- */}
      {questions && questions.length > 0 && !result && (
        <div className="mt-5 border-t border-kyck-200/70 pt-4">
          <SectionHeading step="2" title="A couple of questions first" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {questions.map((q) => (
              <div key={q.id} className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="mb-2 text-sm font-medium text-slate-800">{q.question}</p>
                <div className="flex flex-wrap gap-1">
                  {q.options.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setPicked((p) => ({ ...p, [q.id]: option }))}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs transition-colors',
                        picked[q.id] === option
                          ? 'border-kyck-400 bg-kyck-50 text-kyck-700'
                          : 'border-slate-200 text-slate-600 hover:border-kyck-300',
                      )}
                    >
                      {option}
                    </button>
                  ))}
                  {/* Free text always available — the options are shortcuts, not
                      a closed list. */}
                  <button
                    type="button"
                    onClick={() => setPicked((p) => ({ ...p, [q.id]: OTHER }))}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs transition-colors',
                      picked[q.id] === OTHER
                        ? 'border-kyck-400 bg-kyck-50 text-kyck-700'
                        : 'border-slate-200 text-slate-600 hover:border-kyck-300',
                    )}
                  >
                    Something else…
                  </button>
                </div>
                {picked[q.id] === OTHER && (
                  <input
                    type="text"
                    aria-label={`Your answer to: ${q.question}`}
                    value={typed[q.id] ?? ''}
                    placeholder="Type your answer"
                    className="mt-2 block h-8 w-full rounded-md border border-slate-200 px-2 text-sm"
                    onChange={(e) => setTyped((t) => ({ ...t, [q.id]: e.target.value }))}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              onClick={() => generate.mutate(answersFor(questions))}
              disabled={busy}
            >
              {generate.isPending ? <Spinner className="h-3.5 w-3.5" /> : null}
              Write the captions
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => generate.mutate([])}
              disabled={busy}
            >
              Skip these
            </Button>
            <span className="text-xs text-slate-500">
              Answer what&apos;s useful — anything you leave blank is simply not sent.
            </span>
          </div>
        </div>
      )}

      {/* ---------------- 3 · Drafts ---------------- */}
      {result && (
        <div className="mt-5 border-t border-kyck-200/70 pt-4">
          <div className="mb-2 flex flex-wrap items-baseline gap-2">
            <SectionHeading step="3" title="AI-generated drafts" />
            <span className="text-xs text-slate-500">— pick one to edit in the caption box</span>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {result.drafts.map((draft, index) => (
              <article
                key={`${index}-${draft.caption.slice(0, 24)}`}
                className={cn(
                  'flex flex-col rounded-lg border bg-white p-4',
                  used === index ? 'border-kyck-400 ring-1 ring-kyck-200' : 'border-slate-200',
                )}
              >
                <div className="mb-2 flex items-baseline gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-kyck-700">
                    {captionStyleLabel(draft.style)}
                  </span>
                  <span className="text-[11px] text-slate-400">
                    {CAPTION_DRAFT_STYLES.find((s) => s.id === draft.style)?.blurb}
                  </span>
                </div>
                {/* Readable width and full height — the whole point of the
                    workspace is that a several-hundred-word draft can be read
                    and compared rather than squinted at in a narrow box. */}
                <p className="flex-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
                  {draft.caption}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={used === index ? 'default' : 'outline'}
                    onClick={() => {
                      onUse(draft.caption);
                      setUsed(index);
                    }}
                  >
                    {used === index ? 'Added to the caption' : 'Use this one'}
                  </Button>
                  <span className="text-[11px] text-slate-400">
                    {draft.caption.length.toLocaleString()} characters
                  </span>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-3 space-y-1">
            <ReferenceReport read={result.readReferences} skipped={result.skippedReferences} />
            <p className="text-xs text-slate-500">
              {groundingLabel(result.grounding, result.exampleCount)}
              {result.imageUsed === 'video-thumbnail' &&
                ' The AI saw a still frame, not the video.'}{' '}
              Check every fact — the AI is told never to invent prices, finance terms or
              availability, but it can still be wrong.
            </p>
          </div>
        </div>
      )}

      {/* ---------------- 4 · Refine ---------------- */}
      {currentCaption.trim() !== '' && (
        <div className="mt-5 border-t border-kyck-200/70 pt-4">
          <SectionHeading step="4" title="Refine the caption" />
          <div className="flex max-w-2xl items-center gap-1.5">
            <Wand2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <input
              id="caption-refine"
              aria-label="Refine the caption"
              type="text"
              value={instruction}
              placeholder="punchier · add bullet points · shorter · lead with the price"
              className="block h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
              onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && instruction.trim() !== '' && !refine.isPending) {
                  e.preventDefault();
                  refine.mutate();
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="shrink-0"
              disabled={refine.isPending || instruction.trim() === ''}
              onClick={() => refine.mutate()}
            >
              {refine.isPending ? <Spinner className="h-3.5 w-3.5" /> : 'Refine'}
            </Button>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">
            Revises whatever is in the caption box right now — including your own edits. Ask again
            to keep going.
          </p>
          {refineError && <p className="mt-1 text-xs text-amber-700">{refineError}</p>}
          {refineReport && (
            <div className="mt-1">
              <ReferenceReport
                read={refineReport.readReferences}
                skipped={refineReport.skippedReferences}
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}
